/**
 * Иконки — инлайновые SVG.
 *
 * Не эмодзи: эмодзи рисуются шрифтом операционной системы, выглядят по-разному
 * на Windows и macOS, не наследуют цвет текста и не масштабируются вместе с
 * интерфейсом. Не иконочный шрифт и не спрайт с внешнего домена: CSP приходит
 * заголовком и внешние источники блокирует.
 *
 * Все иконки наследуют currentColor, поэтому отдельной раскраски под тему не
 * требуют. Декоративные помечены aria-hidden — озвучивать их скринридеру
 * нечего, смысл несёт соседний текст или aria-label кнопки.
 */

interface IconProps {
  className?: string;
}

function base(className?: string) {
  return {
    // Размер по умолчанию задаётся утилитами: отдельного класса .icon
    // больше нет, а иконка без размера схлопывается в ноль.
    className: className ?? "size-[18px] shrink-0",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    /*
     * Толщина линии задаётся явно.
     *
     * Её не было вовсе, а значение SVG по умолчанию — 1. На сетке 24 при
     * выводе в 18 px это даёт штрих около 0,75 px: волосяная линия, которая
     * не держит вес рядом с Inter и на обычном экране местами исчезает в
     * пиксельной сетке. Весь набор из трёх десятков иконок выглядел бледным
     * и невнятным именно поэтому, а не из-за формы.
     *
     * 1,75 — между Lucide (2) и Feather (1,5). Двойка при 18 px уже грубовата
     * для плотного интерфейса, полторы — всё ещё бледны.
     */
    strokeWidth: 1.75,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    focusable: false as const,
  };
}

export function SearchIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

export function SendIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M4.5 12 20 4.5 15.5 20l-4-6.5-7-1.5Z" />
    </svg>
  );
}

export function TrashIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />
    </svg>
  );
}

export function PencilIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17v3Z" />
    </svg>
  );
}

/** Одна галочка: сообщение принято сервером Telegram. */
export function CheckIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="m5 13 4 4 10-10" />
    </svg>
  );
}

/** Две галочки: доставлено. Телеграмная конвенция статуса. */
export function ChecksIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="m2 13 3.5 3.5L13 9" />
      <path d="m11 16.5 1 1L22 7.5" />
    </svg>
  );
}

/** Часы: отправка ещё не подтверждена Telegram. */
export function ClockIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4.5l3 1.5" />
    </svg>
  );
}

export function AlertIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M12 4 2.5 20h19L12 4Z" />
      <path d="M12 10v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

export function LogoutIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" />
      <path d="M10 8 6 12l4 4" />
      <path d="M6 12h9" />
    </svg>
  );
}

export function LockIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <rect x="4.5" y="10" width="15" height="10" rx="2" />
      <path d="M8 10V7a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

export function ChatIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M20 12a7.5 7.5 0 0 1-10.9 6.7L4 20l1.3-4.4A7.5 7.5 0 1 1 20 12Z" />
    </svg>
  );
}

export function ArchiveIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <rect x="3.5" y="4" width="17" height="4.5" rx="1" />
      <path d="M5 8.5V19a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8.5" />
      <path d="M10 12h4" />
    </svg>
  );
}

export function BoltIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M13 3 5.5 13H11l-1 8 8.5-10H13l1-8Z" />
    </svg>
  );
}

export function PlusIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/** Значок боковой панели: рамка с выделенной левой колонкой. */
export function SidebarIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M9 4v16" />
    </svg>
  );
}

export function BellIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}

/** Колокольчик с перечёркиванием — оповещения выключены. */
export function BellOffIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M18 8a6 6 0 0 0-9.3-5" />
      <path d="M6.3 6.3A6 6 0 0 0 6 8c0 7-3 9-3 9h13" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
      <path d="m2 2 20 20" />
    </svg>
  );
}

/** Стрелка ответа — как в Telegram: изгиб влево-вверх. */
export function ReplyIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M9 17 4 12l5-5" />
      <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
    </svg>
  );
}

export function CopyIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </svg>
  );
}

export function PinIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M12 17v5" />
      <path d="M9 10.76V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v5.76a2 2 0 0 0 .59 1.42L17 13.5V17H7v-3.5l1.41-1.32A2 2 0 0 0 9 10.76Z" />
    </svg>
  );
}

export function PinOffIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M12 17v5" />
      <path d="M15 4.5V5v5.76a2 2 0 0 0 .59 1.42L17 13.5V17H9" />
      <path d="m2 2 20 20" />
    </svg>
  );
}

export function ChevronIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="m7 10 5 5 5-5" />
    </svg>
  );
}

export function SmileIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9 10h.01M15 10h.01" />
      <path d="M8.5 14a4.5 4.5 0 0 0 7 0" />
    </svg>
  );
}

export function ImageIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
      <circle cx="9" cy="9.5" r="1.5" />
      <path d="m4 16 4.5-4.5 4 4L16 12l4 4" />
    </svg>
  );
}

export function VideoIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <rect x="3" y="5.5" width="12.5" height="13" rx="2" />
      <path d="m15.5 10 5.5-3v10l-5.5-3z" />
    </svg>
  );
}

export function FileIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M13.5 3.5H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9z" />
      <path d="M13.5 3.5V9H19" />
    </svg>
  );
}

export function MicIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <rect x="9.5" y="3" width="5" height="11" rx="2.5" />
      <path d="M6 11.5a6 6 0 0 0 12 0" />
      <path d="M12 17.5V21" />
    </svg>
  );
}

export function MusicIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M9 18V6l10-2v12" />
      <circle cx="6.5" cy="18" r="2.5" />
      <circle cx="16.5" cy="16" r="2.5" />
    </svg>
  );
}

export function StickerIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M20.5 12a8.5 8.5 0 1 0-8.5 8.5c2 0 8.5-6.5 8.5-8.5Z" />
      <path d="M13.5 20.3V15a1.5 1.5 0 0 1 1.5-1.5h5.3" />
    </svg>
  );
}

export function DownloadIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M12 3.5v11" />
      <path d="m7.5 10.5 4.5 4.5 4.5-4.5" />
      <path d="M4.5 19.5h15" />
    </svg>
  );
}

export function ClipIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M20 11.5 12 19.5a5 5 0 0 1-7-7l8-8a3.5 3.5 0 0 1 5 5l-8 8a2 2 0 0 1-3-3l7.5-7.5" />
    </svg>
  );
}

export function XIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

export function RefreshIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M20 11a8 8 0 1 0-.6 4" />
      <path d="M20 4v7h-7" />
    </svg>
  );
}

export function GlobeIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3.5 9h17M3.5 15h17" />
      <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18" />
    </svg>
  );
}

/** Трубка — обычный звонок. Стрелка сверху различает исходящий и входящий. */
export function PhoneIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M5.5 4h3l1.5 4-2 1.5a11 11 0 0 0 6.5 6.5L16 14l4 1.5v3a2 2 0 0 1-2 2A15 15 0 0 1 3.5 6a2 2 0 0 1 2-2" />
    </svg>
  );
}

export function PhoneOutgoingIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M5.5 4h3l1.5 4-2 1.5a11 11 0 0 0 6.5 6.5L16 14l4 1.5v3a2 2 0 0 1-2 2A15 15 0 0 1 3.5 6a2 2 0 0 1 2-2" />
      <path d="M15 9l5.5-5.5M16 3.5h4.5V8" />
    </svg>
  );
}

export function PhoneIncomingIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M5.5 4h3l1.5 4-2 1.5a11 11 0 0 0 6.5 6.5L16 14l4 1.5v3a2 2 0 0 1-2 2A15 15 0 0 1 3.5 6a2 2 0 0 1 2-2" />
      <path d="M20.5 3.5 15 9M15 4.5V9h4.5" />
    </svg>
  );
}

/** Пропущенный/отменённый — крестик вместо стрелки. */
export function PhoneMissedIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M5.5 4h3l1.5 4-2 1.5a11 11 0 0 0 6.5 6.5L16 14l4 1.5v3a2 2 0 0 1-2 2A15 15 0 0 1 3.5 6a2 2 0 0 1 2-2" />
      <path d="m15.5 3.5 5 5M20.5 3.5l-5 5" />
    </svg>
  );
}

/** Показать/скрыть пароль. */
export function EyeIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M2.5 12s3.5-6.5 9.5-6.5 9.5 6.5 9.5 6.5-3.5 6.5-9.5 6.5S2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function EyeOffIcon({ className }: IconProps) {
  return (
    <svg {...base(className)}>
      <path d="M3 3l18 18" />
      <path d="M10.6 6.2A10 10 0 0 1 12 6c6 0 9.5 6 9.5 6a17 17 0 0 1-3.2 3.9" />
      <path d="M6.6 6.7A16.6 16.6 0 0 0 2.5 12s3.5 6 9.5 6c1.6 0 3-.4 4.2-1" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </svg>
  );
}
